import { type } from 'arktype';

/**
 * Activity type ("Tätigkeitsart") — global master data with a billable flag.
 * In use (project assignments or time entries) it cannot be deleted, only
 * archived via `archived: true` on update.
 */
export interface ActivityType {
  id: string;
  name: string;
  billable: boolean;
  sortOrder: number;
  /** ISO timestamp when the type was archived; `null` = active. */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const activityTypeNameSchema = type('string>=1 & string<=200').configure({
  description: 'Display name, unique across all activity types (case-insensitive).',
  examples: ['Entwicklung'],
});

export const createActivityTypeSchema = type({
  name: activityTypeNameSchema,
  'billable?': type('boolean').configure({
    description: 'Whether time booked on this activity is billable by default. Defaults to `true`.',
  }),
  'sortOrder?': type('number').configure({
    description: 'Sort order in pickers and lists. Lower numbers appear first. Defaults to `0`.',
    examples: [10],
  }),
}).configure({ description: 'Payload for creating a new activity type.' });
export type CreateActivityType = typeof createActivityTypeSchema.t;

export const updateActivityTypeSchema = type({
  'name?': activityTypeNameSchema,
  'billable?': type('boolean').configure({
    description: 'Updated billable default.',
  }),
  'sortOrder?': type('number').configure({
    description: 'Updated sort order.',
    examples: [10],
  }),
  'archived?': type('boolean').configure({
    description:
      'Archive (`true`) or restore (`false`) the activity type. Archived types stay readable but disappear from selection lists.',
  }),
}).configure({ description: 'Partial update for an activity type.' });
export type UpdateActivityType = typeof updateActivityTypeSchema.t;
